import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import "../styles/BeforeAndAfterPage.scss";
import { useImagesByTreatmentName } from "../Apis/useApiQueries";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faExpand,
  faTimes,
  faImages,
  faEye,
  faThLarge,
  faPlay,
  faPause,
} from "@fortawesome/free-solid-svg-icons";
import { metaPixelEvents } from "../utils/metaPixel";

const BeforeAndAfter = () => {
  const { treatmentName } = useParams();
  const { data: images = [], error: imagesError } =
    useImagesByTreatmentName(treatmentName);

  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const [viewMode, setViewMode] = useState("carousel"); // 'carousel' or 'grid'
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);

  // Touch/swipe functionality for modal
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Track before/after image gallery view
  useEffect(() => {
    if (images.length > 0 && treatmentName) {
      metaPixelEvents.trackViewContentImages(treatmentName);
      setIsLoading(false);
    }
  }, [images, treatmentName]);

  // Handle image loading states
  const handleImageLoad = (index) => {
    setImageLoadingStates((prev) => ({
      ...prev,
      [index]: true,
    }));
  };

  // Open modal and set selected image
  const openModal = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
    setIsModalOpen(true);
    document.body.classList.add("prevent-scroll");
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
    document.body.classList.remove("prevent-scroll");
  };

  // Navigate to next image
  const goToNextImage = () => {
    setCurrentIndex((currentIndex + 1) % images.length);
  };

  // Navigate to previous image
  const goToPreviousImage = () => {
    setCurrentIndex((currentIndex - 1 + images.length) % images.length);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isModalOpen) return;

      switch (e.key) {
        case "Escape":
          closeModal();
          break;
        case "ArrowLeft":
          goToPreviousImage();
          break;
        case "ArrowRight":
          goToNextImage();
          break;
        default:
          break;
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, [isModalOpen, currentIndex]);

  // Toggle between view modes
  const toggleViewMode = (mode) => {
    setViewMode(mode);
    if (mode === "carousel") {
      setCarouselIndex(0);
    }
  };

  // Carousel navigation
  const nextCarouselImage = useCallback(() => {
    setCarouselIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const prevCarouselImage = useCallback(() => {
    setCarouselIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  // Auto-play carousel
  useEffect(() => {
    if (isAutoPlay && viewMode === "carousel" && images.length > 0) {
      const interval = setInterval(nextCarouselImage, 3000);
      return () => clearInterval(interval);
    }
  }, [isAutoPlay, viewMode, images.length, nextCarouselImage]);

  // Toggle auto-play
  const toggleAutoPlay = () => {
    setIsAutoPlay(!isAutoPlay);
  };

  // Touch handlers for swipe functionality
  const handleTouchStart = (e) => {
    setTouchEnd(0); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 75; // Increased threshold for more deliberate swipes
    const isRightSwipe = distance < -75;

    // Only trigger navigation if there are multiple images
    if (images.length > 1) {
      if (isLeftSwipe) {
        goToNextImage();
      } else if (isRightSwipe) {
        goToPreviousImage();
      }
    }

    // Reset touch positions
    setTouchStart(0);
    setTouchEnd(0);
  };

  if (imagesError) {
    return (
      <div className="before-after-page">
        <div className="error-container">
          <div className="error-message">
            <h3>שגיאה בטעינת התמונות</h3>
            <FontAwesomeIcon icon={faImages} className="error-icon" />
            <p>{imagesError.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="before-after-page" dir="rtl">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="page-title">דוגמאות לפני ואחרי</h1>
            <h2 className="treatment-title">{treatmentName}</h2>
            <p className="hero-description">
              צפו בתוצאות המדהימות של הטיפולים שלנו
            </p>
          </div>
        </div>
        <div className="hero-decoration"></div>
      </section>

      {/* Gallery Section */}
      <section className="gallery-section">
        <div className="container">
          {isLoading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>טוען תמונות...</p>
            </div>
          ) : images.length > 0 ? (
            <>
              <div className="gallery-header">
                <h3 className="gallery-title">גלריית תמונות</h3>
                <p className="gallery-subtitle">
                  {viewMode === "carousel"
                    ? "השתמשו בחצים לניווט או לחצו על תמונה לצפייה מוגדלת"
                    : "לחצו על תמונה לצפייה מוגדלת • השתמשו בכפתורי התצוגה למטה לשינוי הפריסה"}
                </p>
              </div>

              {viewMode === "carousel" ? (
                <div className="carousel-container">
                  <div className="carousel-main">
                    <button
                      className="carousel-nav prev"
                      onClick={prevCarouselImage}
                      disabled={images.length <= 1}
                    >
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>

                    <div className="carousel-image-container">
                      <img
                        src={images[carouselIndex]}
                        alt={`Before and After - ${treatmentName}`}
                        className="carousel-main-image"
                        onClick={() =>
                          openModal(images[carouselIndex], carouselIndex)
                        }
                      />
                      <div
                        className="carousel-overlay"
                        onClick={() =>
                          openModal(images[carouselIndex], carouselIndex)
                        }
                      >
                        <FontAwesomeIcon
                          icon={faExpand}
                          className="expand-icon"
                        />
                      </div>
                    </div>

                    <button
                      className="carousel-nav next"
                      onClick={nextCarouselImage}
                      disabled={images.length <= 1}
                    >
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                  </div>

                  <div className="carousel-controls">
                    <div className="carousel-info">
                      <span className="image-counter">
                        {carouselIndex + 1} מתוך {images.length}
                      </span>
                      <button
                        className="autoplay-btn"
                        onClick={toggleAutoPlay}
                        title={
                          isAutoPlay
                            ? "עצור הצגה אוטומטית"
                            : "התחל הצגה אוטומטית"
                        }
                      >
                        <FontAwesomeIcon icon={isAutoPlay ? faPause : faPlay} />
                      </button>
                    </div>
                  </div>

                  <div className="carousel-thumbnails">
                    {images.map((imageUrl, index) => (
                      <div
                        key={`thumb-${index}`}
                        className={`thumbnail ${
                          index === carouselIndex ? "active" : ""
                        }`}
                        onClick={() => setCarouselIndex(index)}
                      >
                        <img src={imageUrl} alt={`תמונה ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="grid-container">
                  {images.map((imageUrl, index) => (
                    <div
                      key={`${imageUrl}-${index}`}
                      className={`grid-item ${
                        imageLoadingStates[index] ? "loaded" : "loading"
                      }`}
                      onClick={() => openModal(imageUrl, index)}
                    >
                      <div className="image-container">
                        <img
                          src={imageUrl}
                          alt={`Before and After - ${treatmentName}`}
                          onLoad={() => handleImageLoad(index)}
                          loading="lazy"
                        />
                        <div className="hover-overlay">
                          <div className="overlay-icon">
                            <FontAwesomeIcon icon={faExpand} />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="gallery-actions">
                <div className="view-mode-toggle">
                  <button
                    className={`view-btn ${
                      viewMode === "carousel" ? "active" : ""
                    }`}
                    onClick={() => toggleViewMode("carousel")}
                  >
                    <span>קרוסלה</span>
                    <FontAwesomeIcon icon={faImages} />
                  </button>
                  <button
                    className={`view-btn ${
                      viewMode === "grid" ? "active" : ""
                    }`}
                    onClick={() => toggleViewMode("grid")}
                  >
                    <span>רשת</span>
                    <FontAwesomeIcon icon={faThLarge} />
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h3>אין תמונות זמינות</h3>
              <FontAwesomeIcon icon={faImages} className="empty-icon" />
              <p>לא נמצאו תמונות עבור הטיפול הזה כרגע</p>
            </div>
          )}
        </div>
      </section>

      {/* Enhanced Modal */}
      {isModalOpen && selectedImage && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-container">
            <div className="modal-header">
              <div className="modal-title">
                <span>{treatmentName}</span>
                <FontAwesomeIcon icon={faEye} />
              </div>
              {images.length > 1 && (
                <div className="modal-controls-info">
                  <span className="image-counter">
                    {currentIndex + 1} / {images.length}
                  </span>
                  <span className="swipe-hint">החליקו לניווט</span>
                </div>
              )}
              <button className="close-button" onClick={closeModal}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div
              className="modal-content"
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <img
                key={currentIndex}
                src={images[currentIndex]}
                alt={`Before and After - ${treatmentName}`}
                className="modal-image"
              />

              {images.length > 1 && (
                <div className="navigation-arrows">
                  <button
                    className="arrow prev-arrow"
                    onClick={goToPreviousImage}
                    title="תמונה קודמת"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                  <button
                    className="arrow next-arrow"
                    onClick={goToNextImage}
                    title="תמונה הבאה"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeforeAndAfter;
